import { useTranslation } from 'react-i18next'
import { Button } from "src/@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "src/@/components/ui/dialog"
import { Input } from "src/@/components/ui/input"
import { Label } from "src/@/components/ui/label"
import { RowInput } from "src/components/forms/RowInput"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/@/components/ui/select"

// Cores do shadcn/ui (Tailwind CSS)
// Apenas cores padrão do shadcn/ui
const CORES_MAP = {
  slate: { tailwindClass: 'bg-slate-200' },
  orange: { tailwindClass: 'bg-orange-200' },
  yellow: { tailwindClass: 'bg-yellow-200' },
  cyan: { tailwindClass: 'bg-cyan-200' },
  blue: { tailwindClass: 'bg-blue-200' },
  purple: { tailwindClass: 'bg-purple-200' },
  pink: { tailwindClass: 'bg-pink-200' },
  red: { tailwindClass: 'bg-red-200' }
}

// Função para obter classe Tailwind a partir do nome da cor
export function getColorClass(colorName) {
  if (!colorName) return 'bg-gray-500'
  // Se for um hex antigo (compatibilidade), retorna vazio para usar inline style
  if (colorName.startsWith('#')) return ''
  return CORES_MAP[colorName]?.tailwindClass || 'bg-gray-500'
}

// Função para converter hex antigo para nome de cor (compatibilidade)
export function convertHexToColorName(hex) {
  if (!hex || !hex.startsWith('#')) return hex // Já é um nome ou inválido
  // Se for hex antigo, retorna gray como padrão (não há mais mapeamento hex->nome)
  return 'gray'
}

// Função para determinar a cor do texto baseada no nome da cor
function getTextColor(colorName) {
  // Cores claras que precisam de texto preto
  const lightColors = ['yellow', 'amber', 'lime'];
  return lightColors.includes(colorName) ? 'text-black' : 'text-white';
}

export default function ModalTags({
  open,
  onClose,
  onSubmit,
  action,
  nome,
  changeNome,
  corHex, // Recebe o nome da cor (ex: "red", "slate")
  changeCorHex
}) {
  const { t } = useTranslation('settings');

  // Nomes de cor traduzidos (dependem de t, por isso não ficam no CORES_MAP em nível de módulo)
  const CORES_NOME_MAP = {
    slate: t('tags.modal.colors.slate', 'Cinza'),
    orange: t('tags.modal.colors.orange', 'Laranja'),
    yellow: t('tags.modal.colors.yellow', 'Amarelo'),
    cyan: t('tags.modal.colors.cyan', 'Ciano'),
    blue: t('tags.modal.colors.blue', 'Azul'),
    purple: t('tags.modal.colors.purple', 'Roxo'),
    pink: t('tags.modal.colors.pink', 'Rosa'),
    red: t('tags.modal.colors.red', 'Vermelho'),
  }

  const CORES_PREDEFINIDAS = Object.keys(CORES_MAP).map(key => ({
    value: key, // Nome da cor (ex: "red", "slate", "rose")
    label: key,
    nome: CORES_NOME_MAP[key],
    tailwindClass: CORES_MAP[key].tailwindClass
  }))

  const corSelecionada = CORES_MAP[corHex]
  const colorClass = `${corSelecionada?.tailwindClass}` || 'bg-gray-500'
  const nomeCorSelecionada = CORES_NOME_MAP[corHex]

  return (
    <Dialog open={open} onOpenChange={onClose}>

      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {action === "Criar" ? t('tags.modal.createTitle', 'Nova Tag') : t('tags.modal.editTitle', 'Editar Tag')}
          </DialogTitle>

        </DialogHeader>
        <div className="grid gap-4 py-4">
          <RowInput
            label={t('labels.name', { ns: 'common' })}
            input={<Input
              onChange={changeNome}
              value={nome}
            />}
          />
          <RowInput
            label={t('tags.modal.colorLabel', 'Cor')}
            input={
              <Select value={corHex} onValueChange={changeCorHex}>
                <SelectTrigger>
                  <SelectValue placeholder={t('tags.modal.colorPlaceholder', 'Selecione uma cor')}>
                    {corHex && corSelecionada && (
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-5 h-5 rounded border border-gray-300 ${colorClass}`}
                        />
                        <span>
                          {nomeCorSelecionada}
                        </span>
                      </div>
                    )}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {CORES_PREDEFINIDAS.map((cor) => (
                    <SelectItem key={cor.value} value={cor.value}>
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-5 h-5 rounded border border-gray-300 flex-shrink-0 ${cor.tailwindClass}`}
                        />
                        <span>{cor.nome}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            }
          />

          {/* Preview da etiqueta */}
          <div className="pt-2">
            <Label className="text-sm text-gray-600 mb-2 block">{t('tags.modal.previewLabel', 'Preview')}</Label>
            <div className="flex items-center gap-2 p-3 border border-gray-200 rounded-md bg-gray-50">
              <span
                className={`inline-flex text-gray-800 p-1 items-center rounded-sm text-xs font-medium ${colorClass} ${getTextColor(corHex)}`}
              >
                {nome || t('tags.modal.previewPlaceholder', 'Nome da etiqueta')}
              </span>
              <span className="text-xs text-gray-500">
                {t('tags.modal.previewHint', 'Como ficará a etiqueta')}
              </span>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={onSubmit}>{action === "Criar" ? t('buttons.create', { ns: 'common' }) : t('buttons.save', { ns: 'common' })}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
